import { useEffect, useState } from 'react';
// import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { ReviewLog } from '@/types/reviewLog';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';

interface ReviewLogModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    reviewLog?: ReviewLog | null; // For editing
    onSave: () => void;
}

export function ReviewLogModal({ open, onOpenChange, reviewLog, onSave }: ReviewLogModalProps) {
    const [summary, setSummary] = useState('');
    const [abstraction, setAbstraction] = useState('');
    const [entityCode, setEntityCode] = useState('');
    const [reviewDate, setReviewDate] = useState(new Date());
    const [status, setStatus] = useState('ACTIVE');
    const [entities, setEntities] = useState<any[]>([]);
    const [isPreview, setIsPreview] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isRolePickerOpen, setIsRolePickerOpen] = useState(false);
    const [isAbstractionOpen, setIsAbstractionOpen] = useState(false);

    useEffect(() => {
        if (open) {
            // Load entities
            api.getEntities().then(res => {
                const allEntities = res.data || [];
                // Filter 16.xx
                const filtered = allEntities.filter((e: any) => e.code.startsWith('16.'));
                setEntities(filtered);
            });

            if (reviewLog) {
                setSummary(reviewLog.summary);
                setAbstraction(reviewLog.abstraction || '');
                setEntityCode(reviewLog.entity_code);
                setReviewDate(new Date(reviewLog.review_date));
                setStatus(reviewLog.status);
                setIsPreview(true); // Default to preview mode when viewing existing log
            } else {
                setSummary('');
                setAbstraction('');
                setEntityCode('');
                setReviewDate(new Date());
                setStatus('ACTIVE');
                setIsPreview(false); // Default to edit mode when creating new log
            }
        }
    }, [open, reviewLog]);

    const handleSubmit = async () => {
        const data = {
            summary,
            abstraction,
            entity_code: entityCode,
            review_date: `${format(reviewDate, 'yyyy-MM-dd')}T12:00:00Z`,
            status
        };

        try {
            if (reviewLog) {
                await api.updateReviewLog(reviewLog.id, data);
            } else {
                await api.createReviewLog(data);
            }
            onSave();
            onOpenChange(false);
        } catch (e) {
            console.error(e);
            alert('Failed to save review log');
        }
    };

    // --- Content Renderer ---
    const renderEditorContent = () => (
        <div className="flex-1 flex flex-col h-full bg-white dark:bg-zinc-950 overflow-hidden relative">
            {isAbstractionOpen ? (
                <div className="flex-1 flex flex-col w-full h-full animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <div className="flex items-center justify-between p-4 border-b border-zinc-100 dark:border-zinc-900 shrink-0">
                        <button
                            onClick={() => setIsAbstractionOpen(false)}
                            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                            <span className="font-medium">Back</span>
                        </button>
                        <span className="font-medium text-zinc-900 dark:text-zinc-100">Abstraction Editor</span>
                        <div className="w-20" />
                    </div>
                    <div className="flex-1 p-8 overflow-y-auto">
                        <textarea
                            value={abstraction}
                            onChange={(e) => setAbstraction(e.target.value)}
                            className="w-full h-full bg-transparent border-none focus:ring-0 outline-none p-0 text-base leading-relaxed text-zinc-900 dark:text-zinc-100 resize-none placeholder:text-zinc-300 dark:placeholder:text-zinc-700 font-mono"
                            placeholder="Write your abstraction here..."
                            autoFocus
                        />
                    </div>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto p-4 md:p-8 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-zinc-500">Summary (Markdown)</span>
                        <Button variant="ghost" size="sm" onClick={() => setIsPreview(!isPreview)}>
                            {isPreview ? 'Edit' : 'Preview'}
                        </Button>
                    </div>

                    <div className="flex-1 min-h-[200px] rounded-md overflow-hidden relative bg-background">
                        {isPreview ? (
                            <div className="prose dark:prose-invert max-w-none p-4 h-full overflow-y-auto prose-headings:m-0 prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-p:text-sm prose-li:text-sm [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                                <ReactMarkdown>{summary}</ReactMarkdown>
                            </div>
                        ) : (
                            <Textarea
                                id="summary"
                                value={summary}
                                onChange={(e) => setSummary(e.target.value)}
                                className="w-full h-full resize-none border-0 focus-visible:ring-0 shadow-none p-4 font-mono text-sm [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']"
                                placeholder="# Summary..."
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Toolbar */}
            <div className="p-4 pb-8 md:pb-4 flex flex-col gap-3 bg-white dark:bg-zinc-950 border-t border-zinc-100 dark:border-zinc-900 z-20 shrink-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex gap-1.5 items-center flex-wrap">
                        {/* AI Role Selector */}
                        <Popover open={isRolePickerOpen} onOpenChange={setIsRolePickerOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="flex items-center gap-2 px-3 py-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors"
                                    title="Select AI Role"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                        <circle cx="12" cy="7" r="4" />
                                    </svg>
                                    {entityCode && (
                                        <span className="text-sm font-medium">
                                            {entityCode}
                                        </span>
                                    )}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[350px] p-0 z-[110]" align="start">
                                <div className="max-h-[300px] overflow-y-auto">
                                    {entities.map(e => (
                                        <button
                                            key={e.code}
                                            onClick={() => {
                                                setEntityCode(e.code);
                                                setIsRolePickerOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors border-b border-zinc-100 dark:border-zinc-800 last:border-0 ${entityCode === e.code ? 'bg-zinc-50 dark:bg-zinc-800/50 text-zinc-900 dark:text-zinc-100' : 'text-zinc-600 dark:text-zinc-400'
                                                }`}
                                        >
                                            <span className="font-medium">{e.code}</span>
                                            <span className="text-xs text-zinc-400 dark:text-zinc-500 ml-2">({e.alias || e.description})</span>
                                        </button>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>

                        {/* Abstraction Toggle */}
                        <button
                            onClick={() => setIsAbstractionOpen(true)}
                            className={`flex items-center gap-1.5 px-2.5 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors ${abstraction && abstraction.trim().length > 0 ? 'text-purple-600 dark:text-purple-400' : 'text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'}`}
                            title="Edit Abstraction"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill={abstraction && abstraction.trim().length > 0 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                        </button>

                        {/* Date Selector */}
                        <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="flex items-center gap-2 px-3 py-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors"
                                    title="Change date"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    <span className="text-sm font-medium">
                                        {format(reviewDate, "MMM d, yyyy")}
                                    </span>
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-[110]" align="start">
                                <Calendar
                                    mode="single"
                                    selected={reviewDate}
                                    onSelect={(newDate) => {
                                        if (newDate) {
                                            setReviewDate(newDate);
                                            setIsDatePickerOpen(false);
                                        }
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    <button
                        onClick={handleSubmit}
                        className="px-6 py-2.5 text-sm font-medium text-white bg-zinc-900 rounded-full hover:bg-zinc-800 dark:bg-zinc-50 dark:text-zinc-900 transition-all hover:shadow-lg active:scale-95"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex flex-col justify-end">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => onOpenChange(false)} />
            <div className="relative w-full h-[85vh] bg-white dark:bg-zinc-950 shadow-2xl border-t border-zinc-200 dark:border-zinc-800 animate-in slide-in-from-bottom duration-300 flex flex-col rounded-t-xl overflow-hidden pb-safe">
                <div className="flex justify-center pt-2 pb-1" onDoubleClick={() => onOpenChange(false)} >
                    <div className="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
                </div>
                {renderEditorContent()}
            </div>
        </div>
    );
}
